// THE GOAL A LEG BELONGS TO (plan 101-multi-repo-goal-legs D8, slice 08). One run per leg
// (D1) means the product leg's box never sees the infra checkout; what it needs from its
// siblings is what they promise or already did — their repo, title, acceptance, PR, merge sha
// and deploy state. `TaskEnvelope.plan_context` carries it.
//
// STAMPED AT CLAIM, ON THE ASSIGNMENT ONLY (`apps/worker` `dispatch/plan-context.ts`), in the
// claim transaction that writes the push, never persisted into `tasks.envelope`: a redispatch
// must see its siblings' state at ITS claim, not at the first one's.
//
// OPTIONAL, NOT A FLAG: a task that is no plan leg (a review, a babysit watch) has no goal, so
// the field is absent; whether it is present is decided by the `plan_groups` row, never a knob.
//
// PRESENCE OF EACH LEG FIELD is the fact, never a guess: `pr` only when the leg's task names a
// PR the projection holds, `deploy` only when that PR has a merge commit, `edge_to_me` only when
// this leg waits on that one. Statuses ride as bounded TEXT, not this file's enums, so a status
// member added later never makes a lagging box refuse the whole assignment
// (`scripts/closed-set-release.ts`); the box renders them, it never branches on them.
//
// Pure, zod-only imports: `../plan-edge` and `../plan-execution` reach nothing internal, so this
// module cannot close an import cycle with `./envelope`, which imports it.

import { z } from 'zod';
import { PlanEdgeHoldSchema, type PlanEdgeRelease, PlanEdgeReleaseSchema } from '../plan-edge';
import { isPlanFilesGroupId } from '../plan-execution';

/**
 * The most legs one context carries: the triage per-ask group cap (12, `TRIAGE_MAX_GROUPS`,
 * `packages/agent`) plus as many again of repair headroom (slice 10 appends legs to the same
 * plan). The bound is the ENVELOPE'S SIZE ON THE NATS SUBJECT, never a cost: a leg is at most
 * ~3 KB (2 KB of acceptance), so 24 legs stay ~72 KB inside the 1 MiB `NATS_MAX_PAYLOAD_BYTES`
 * the whole assignment shares. A plan past it keeps THIS leg and the legs it waits on first and
 * says how many it left out (`legs_total`), so a long repair chain never makes a claim unsendable.
 */
export const PLAN_CONTEXT_LEGS_MAX = 24;

/** One leg's acceptance, rendered, in chars. Longer is cut with {@link PLAN_CONTEXT_TRUNCATED}. */
export const PLAN_CONTEXT_ACCEPTANCE_MAX = 2_000;

/** A leg's (and the goal's) title, in chars — the envelope's own `title` bound. */
export const PLAN_CONTEXT_TITLE_MAX = 200;

/** The marker a cut field ends with, so a reader knows the text did not end there. */
export const PLAN_CONTEXT_TRUNCATED = ' [truncated]' as const;

/** What the box knows of a leg's deploy (D3's newest-verdict read, judged once at claim). */
export const PlanContextDeploy = {
  Deployed: 'deployed',
  Failed: 'failed',
  Pending: 'pending',
  Unknown: 'unknown',
} as const;
export const PlanContextDeploySchema = z.enum(PlanContextDeploy);
export type PlanContextDeploy = z.infer<typeof PlanContextDeploySchema>;

/** A leg's repo visibility as the account's `repositories` row states it; `unknown` = no row. */
export const PlanContextRepoVisibility = {
  Public: 'public',
  Private: 'private',
  Unknown: 'unknown',
} as const;
export const PlanContextRepoVisibilitySchema = z.enum(PlanContextRepoVisibility);
export type PlanContextRepoVisibility = z.infer<typeof PlanContextRepoVisibilitySchema>;

const BoundedStatus = z.string().min(1).max(32);

export const PlanContextLegSchema = z.object({
  group_id: z.string().min(1).max(200),
  repo_full_name: z.string().min(1).max(300),
  /**
   * The leg's repo visibility off `repositories.private` on the account; `unknown` when the
   * account holds no row for it. The PR body names a sibling only when this leg's repo is
   * `private` or the sibling's is `public` (`apps/runner` `harness/plan-context.ts`), so an
   * `unknown` on either side never names a repo; the brief, never posted, lists every leg.
   */
  repo_visibility: PlanContextRepoVisibilitySchema,
  title: z.string().min(1).max(PLAN_CONTEXT_TITLE_MAX),
  /** The leg's acceptance criteria, one `- ` line each, cut at {@link PLAN_CONTEXT_ACCEPTANCE_MAX}. */
  acceptance: z.string().max(PLAN_CONTEXT_ACCEPTANCE_MAX),
  /** The `plan_groups.status` (`PlanGroupStatus`), as text. */
  status: BoundedStatus,
  /** The leg's task status (`TaskStatus`), as text; absent when no task is linked yet. */
  task_status: BoundedStatus.optional(),
  /** The edge THIS leg waits on that leg through; absent when there is none. */
  edge_to_me: z
    .object({
      release: PlanEdgeReleaseSchema,
      environment: z.string().min(1).max(200).nullable(),
      hold: PlanEdgeHoldSchema,
    })
    .optional(),
  pr: z
    .object({
      number: z.number().int().positive(),
      /** A GitHub pull URL; absent on another forge, where the box names the number only. */
      url: z.string().min(1).max(500).optional(),
      state: BoundedStatus,
      merge_commit_sha: z.string().min(7).max(64).optional(),
    })
    .optional(),
  deploy: PlanContextDeploySchema.optional(),
});
export type PlanContextLeg = z.infer<typeof PlanContextLegSchema>;

export const PlanContextSchema = z
  .object({
    plan_id: z.string().min(1).max(200),
    /** The goal's headline: the lead leg's title (`plans` carries no title column). */
    title: z.string().min(1).max(PLAN_CONTEXT_TITLE_MAX),
    this_group_id: z.string().min(1).max(200),
    /** How many legs the plan holds; above `legs.length` exactly when the cap left some out. */
    legs_total: z.number().int().positive(),
    /** Ordered by the plan's own `position`. */
    legs: z.array(PlanContextLegSchema).min(1).max(PLAN_CONTEXT_LEGS_MAX),
  })
  .refine((c) => c.legs.some((l) => l.group_id === c.this_group_id), {
    message: 'plan_context.legs must carry this_group_id — the leg the run IS',
  })
  .refine((c) => c.legs_total >= c.legs.length, {
    message: 'plan_context.legs_total is below the legs it carries',
  });
export type PlanContext = z.infer<typeof PlanContextSchema>;

/** One `plan_groups` row of the leg's plan, as `readPlanContextForTask` (`@developerz/db`) reads it. */
export type PlanContextLegRow = {
  readonly groupId: string;
  readonly repoFullName: string;
  /** `repositories.private` for the leg's repo on the account; `null` when no row is found. */
  readonly repoPrivate: boolean | null;
  readonly title: string;
  readonly position: number;
  readonly groupStatus: string;
  readonly taskStatus: string | null;
  /** The group envelope's `acceptance_criteria`; `null` when the stored envelope has none. */
  readonly acceptance: readonly string[] | null;
  readonly edgeToMe: {
    readonly release: string;
    readonly environment: string | null;
    readonly hold: string;
  } | null;
  readonly pr: {
    readonly number: number;
    readonly state: string;
    readonly mergeCommitSha: string | null;
    /** The leg task's `envelope.forge`; `null` = GitHub. */
    readonly forge: string | null;
  } | null;
  /** The standing verdict's conclusion for the merge commit (see {@link deployOf}); `null` = no row. */
  readonly deployConclusion: string | null;
};

function cut(text: string, max: number): string {
  if (text.length <= max) return text;
  return `${text.slice(0, max - PLAN_CONTEXT_TRUNCATED.length)}${PLAN_CONTEXT_TRUNCATED}`;
}

function acceptanceOf(lines: readonly string[] | null): string {
  if (lines === null || lines.length === 0) return '';
  return cut(lines.map((l) => `- ${l}`).join('\n'), PLAN_CONTEXT_ACCEPTANCE_MAX);
}

/**
 * The deploy fact off the standing verdict. `success` -> deployed, `failure` -> failed, any
 * other stored conclusion -> unknown. No verdict row: `pending` when this leg waits on that one
 * for a deploy (the mint refused a `deployed` edge onto an environment that never deployed, D5,
 * so one is coming), else `unknown` (the repo may not report deploys at all).
 */
function deployOf(row: PlanContextLegRow): PlanContextDeploy | undefined {
  if (row.pr?.mergeCommitSha == null) return undefined;
  if (row.deployConclusion === 'success') return PlanContextDeploy.Deployed;
  if (row.deployConclusion === 'failure') return PlanContextDeploy.Failed;
  if (row.deployConclusion !== null) return PlanContextDeploy.Unknown;
  const waitsForDeploy = row.edgeToMe?.release === ('deployed' satisfies PlanEdgeRelease);
  return waitsForDeploy ? PlanContextDeploy.Pending : PlanContextDeploy.Unknown;
}

function legOf(row: PlanContextLegRow): PlanContextLeg {
  const pr = row.pr;
  const deploy = deployOf(row);
  return {
    group_id: row.groupId,
    repo_full_name: row.repoFullName,
    repo_visibility:
      row.repoPrivate === null
        ? PlanContextRepoVisibility.Unknown
        : row.repoPrivate
          ? PlanContextRepoVisibility.Private
          : PlanContextRepoVisibility.Public,
    title: cut(row.title, PLAN_CONTEXT_TITLE_MAX),
    acceptance: acceptanceOf(row.acceptance),
    status: row.groupStatus,
    ...(row.taskStatus !== null ? { task_status: row.taskStatus } : {}),
    ...(row.edgeToMe !== null
      ? {
          edge_to_me: {
            release: PlanEdgeReleaseSchema.parse(row.edgeToMe.release),
            environment: row.edgeToMe.environment,
            hold: PlanEdgeHoldSchema.parse(row.edgeToMe.hold),
          },
        }
      : {}),
    ...(pr !== null
      ? {
          pr: {
            number: pr.number,
            ...(pr.forge === null || pr.forge === 'github'
              ? { url: `https://github.com/${row.repoFullName}/pull/${pr.number}` }
              : {}),
            state: pr.state,
            ...(pr.mergeCommitSha !== null ? { merge_commit_sha: pr.mergeCommitSha } : {}),
          },
        }
      : {}),
    ...(deploy !== undefined ? { deploy } : {}),
  };
}

const byPosition = (a: PlanContextLegRow, b: PlanContextLegRow): number =>
  a.position - b.position || (a.groupId < b.groupId ? -1 : a.groupId > b.groupId ? 1 : 0);

/**
 * The context for the leg `thisGroupId`, or `null` when the plan holds no other leg (a 1-group
 * plan has no siblings to describe — D10's lone PR task — so the envelope stays as it was).
 *
 * The platform's `plan-files` documents group is bookkeeping, not a leg of the goal: it is left
 * out unless it IS this leg. Over {@link PLAN_CONTEXT_LEGS_MAX}, this leg and the legs it waits
 * on are kept first, then the rest by position. Parsed before it is returned, so a row the wire
 * would refuse throws HERE, inside the claim, naming the plan.
 */
export function buildPlanContext(input: {
  readonly planId: string;
  readonly thisGroupId: string;
  readonly rows: readonly PlanContextLegRow[];
}): PlanContext | null {
  const rows = input.rows
    .filter((r) => r.groupId === input.thisGroupId || !isPlanFilesGroupId(r.groupId))
    .sort(byPosition);
  if (rows.length < 2 || !rows.some((r) => r.groupId === input.thisGroupId)) return null;
  const rank = (r: PlanContextLegRow): number =>
    r.groupId === input.thisGroupId ? 0 : r.edgeToMe !== null ? 1 : 2;
  const kept = [...rows]
    .sort((a, b) => rank(a) - rank(b) || byPosition(a, b))
    .slice(0, PLAN_CONTEXT_LEGS_MAX)
    .sort(byPosition);
  const lead = rows[0];
  const parsed = PlanContextSchema.safeParse({
    plan_id: input.planId,
    title: cut(lead?.title ?? input.planId, PLAN_CONTEXT_TITLE_MAX),
    this_group_id: input.thisGroupId,
    legs_total: rows.length,
    legs: kept.map(legOf),
  });
  if (!parsed.success) {
    throw new Error(
      `buildPlanContext(${input.planId}, ${input.thisGroupId}): the plan's rows do not fit PlanContextSchema — ${parsed.error.message}`,
    );
  }
  return parsed.data;
}
